import type { StudioProjectOutputKind } from "@/features/project/lib/studio-project-plan";
import { findElementInTree } from "@/features/page-editor/store/canvas-slice";
import { useEditorStore } from "@/features/page-editor/store/editor-store";
import { useStudioStore } from "@/store/studio-store";
import type { RailItemId } from "./types";
import type {
  StudioCockpitActivity,
  StudioCockpitDestination,
} from "./cockpit/studio-cockpit-activity";
import {
  SELECTION_BEARING_RAIL_ITEMS,
  buildSelectionChatContext,
  isEditorSelectionTargetAvailable,
} from "./selection-context";
import {
  findStudioAuthorityTask,
  findStudioAuthorityTaskByRailItem,
  getDefaultStudioAuthorityTask,
  studioAuthorityNamespace,
  updateStudioAuthorityDeepLink,
  type StudioAuthorityDomain,
  type StudioAuthorityTask,
} from "./studio-authority-workspaces";

interface ActivateStudioAuthorityTaskOptions {
  domain: StudioAuthorityDomain;
  outputKind: StudioProjectOutputKind;
  requestedView?: string;
  railItem?: RailItemId;
  selection?: StudioCockpitDestination["selection"];
  targetRef?: string;
  attention?: StudioCockpitActivity;
}

export function isStudioAttentionTargetAvailable(
  attention: StudioCockpitActivity,
): boolean {
  const state = useStudioStore.getState();
  if (
    attention.destination.projectId &&
    attention.destination.projectId !== state.currentProjectId
  ) {
    return false;
  }
  return isEditorSelectionTargetAvailable(attention.destination.selection);
}

/**
 * Activates one canonical Studio authority task from any UI entry point.
 *
 * Keeping the rail, chat context, working view, and deep link in one operation
 * prevents a visible surface from disagreeing with Chat/WebMCP authority.
 */
export function activateStudioAuthorityTask({
  domain,
  outputKind,
  requestedView,
  railItem,
  selection,
  targetRef,
  attention,
}: ActivateStudioAuthorityTaskOptions): StudioAuthorityTask {
  const state = useStudioStore.getState();
  const retainedProjectMatches =
    !attention?.destination.projectId ||
    attention.destination.projectId === state.currentProjectId;
  const requestedTask = requestedView
    ? findStudioAuthorityTask(domain, requestedView, outputKind)
    : railItem
      ? findStudioAuthorityTaskByRailItem(domain, railItem, outputKind)
      : getDefaultStudioAuthorityTask(domain, outputKind);
  const task = retainedProjectMatches
    ? requestedTask
    : getDefaultStudioAuthorityTask(domain, outputKind);
  const retainedSelection = selection ?? attention?.destination.selection;
  const previousContext = state.chatContext ?? { mode: "chat" as const };
  // Staying inside one authority domain keeps the Applied intent loop alive,
  // but only where the rail we are landing on can actually show the selection.
  // `SELECTION_BEARING_RAIL_ITEMS` is the same declaration
  // `ConversationTargetSummary` consults before naming a selection, so Chat
  // context and the visible summary cannot disagree about what is selected.
  const retainsSelection =
    previousContext.authorityDomain === domain &&
    SELECTION_BEARING_RAIL_ITEMS.has(task.railItem);
  const selectedAppPage =
    domain === "primary" && outputKind !== "document"
      ? state.currentProject?.manifest?.pages?.find(
          (page) => page.id === state.currentPageId,
        )
      : undefined;
  const appPageId =
    domain === "primary" && outputKind !== "document"
      ? (state.currentPageId ?? undefined)
      : undefined;

  if (domain === "primary") {
    state.setWorkingPageView(outputKind === "document" ? "report" : "app");
  }
  state.setActiveRailItem(task.railItem);
  state.setInspectorPanelOpen(false);
  state.setChatContext({
    ...previousContext,
    authorityDomain: domain,
    authorityView: task.id,
    authorityNamespace: studioAuthorityNamespace(domain),
    primaryKind: outputKind,
    surfaceId: task.surfaceId,
    pageId: appPageId,
    pageName: selectedAppPage?.title ?? appPageId,
    selectionSurface: appPageId ? "page" : undefined,
    selectedElementId: retainsSelection
      ? previousContext.selectedElementId
      : undefined,
    selectedElementLabel: retainsSelection
      ? previousContext.selectedElementLabel
      : undefined,
    selectedBlockName: retainsSelection
      ? previousContext.selectedBlockName
      : undefined,
    selectedBlockLabel: retainsSelection
      ? previousContext.selectedBlockLabel
      : undefined,
    selectedBlockType: retainsSelection
      ? previousContext.selectedBlockType
      : undefined,
    selectedElementType: retainsSelection
      ? previousContext.selectedElementType
      : undefined,
    selectedPropertyKey: retainsSelection
      ? previousContext.selectedPropertyKey
      : undefined,
    operationId: undefined,
    operationLabel: attention?.destination.targetRef ?? targetRef,
    operationKind: undefined,
    operationRequestedAt: undefined,
    operationContract: undefined,
    operationContextFrame: undefined,
    webMcpToolName: undefined,
    studioMcpToolName: undefined,
    webMcpToolStatus: undefined,
    chatOperationInstructions: undefined,
    documentId:
      domain === "guide" || (domain === "primary" && outputKind === "document")
        ? previousContext.documentId
        : undefined,
    documentPageId:
      domain === "guide" || (domain === "primary" && outputKind === "document")
        ? previousContext.documentPageId
        : undefined,
    documentNodeId:
      domain === "guide" || (domain === "primary" && outputKind === "document")
        ? previousContext.documentNodeId
        : undefined,
    documentPageIndex:
      domain === "guide" || (domain === "primary" && outputKind === "document")
        ? previousContext.documentPageIndex
        : undefined,
    documentTarget:
      domain === "guide" || domain === "insights" || outputKind === "document"
        ? previousContext.documentTarget
        : undefined,
    reportId: domain === "insights" ? previousContext.reportId : undefined,
    reportSectionId:
      domain === "insights" ? previousContext.reportSectionId : undefined,
  });
  let retainedTargetAvailable = retainedProjectMatches;
  if (retainedSelection && retainedProjectMatches) {
    const editor = useEditorStore.getState();
    const pageExists =
      !retainedSelection.pageId ||
      Boolean(editor.app?.pages.some((page) => page.id === retainedSelection.pageId));
    const blockExists =
      !retainedSelection.blockName ||
      Boolean(editor.blocks[retainedSelection.blockName]);
    const selectedBlock = retainedSelection.blockName
      ? editor.blocks[retainedSelection.blockName]
      : undefined;
    const targetExists =
      retainedSelection.kind !== "element" ||
      (retainedSelection.elementId != null &&
        selectedBlock != null &&
        Boolean(
          findElementInTree(
            selectedBlock.ui.root,
            retainedSelection.elementId,
          ),
        ));
    retainedTargetAvailable = pageExists && blockExists && targetExists;
    if (retainedTargetAvailable) {
      if (retainedSelection.pageId) editor.setActivePage(retainedSelection.pageId);
      if (retainedSelection.kind === "page" && retainedSelection.pageId) {
        editor.selectPage(retainedSelection.pageId);
      } else if (
        retainedSelection.kind === "block" &&
        retainedSelection.blockName
      ) {
        editor.selectBlock(retainedSelection.blockName);
      } else if (
        retainedSelection.kind === "element" &&
        retainedSelection.elementId &&
        retainedSelection.blockName
      ) {
        editor.select(retainedSelection.elementId, retainedSelection.blockName);
      }
      state.setChatContext(
        buildSelectionChatContext(
          retainedSelection,
          useStudioStore.getState().chatContext ?? { mode: "chat" },
        ),
      );
    }
  }
  if (attention) {
    state.setChatContext({
      ...useStudioStore.getState().chatContext,
      operationId: attention.operationId ?? attention.id,
      operationLabel:
        attention.destination.targetRef ??
        attention.targetRef ??
        attention.title,
      selectedElementLabel:
        attention.destination.selection?.kind === "element"
          ? attention.destination.selection.label
          : useStudioStore.getState().chatContext?.selectedElementLabel,
      chatOperationInstructions: retainedTargetAvailable
        ? undefined
        : "The original target is no longer available. Review the current workspace before continuing.",
    });
  }
  updateStudioAuthorityDeepLink(domain, task.id);
  return task;
}
